import { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { CustomButton, CustomLoadingButton } from "src/design";
import {
  PaymentError,
  resolvePaymentErrorKey,
} from "src/business/payment/paymentErrors";
import { MAX_PROOF_SIZE_MB } from "src/business/payment/proofFile";
import { useReplaceProof } from "src/data/payment/queries/payment";
import { colorTextError } from "src/design/tokens";
import { ProofFileInput } from "./ProofFileInput";

interface ReplaceProofDialogProps {
  open: boolean;
  onClose: () => void;
  restaurantId: string;
  paymentId: string;
  /** Raised on a 409 — an admin resolved the payment while the page was open. */
  onConflict: () => void;
}

/** Swapping in a corrected proof, offered only while the payment is PENDING. */
export const ReplaceProofDialog = ({
  open,
  onClose,
  restaurantId,
  paymentId,
  onConflict,
}: ReplaceProofDialogProps) => {
  const { t } = useTranslation();
  const [proof, setProof] = useState<File | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const replaceProof = useReplaceProof(restaurantId);

  useEffect(() => {
    if (!open) return;
    setProof(null);
    setErrorKey(null);
  }, [open]);

  const handleSubmit = () => {
    if (!proof) return;

    setErrorKey(null);
    replaceProof.mutate(
      { paymentId, proof },
      {
        onSuccess: onClose,
        onError: (error) => {
          setErrorKey(resolvePaymentErrorKey(error));
          if (error instanceof PaymentError && error.status === 409) {
            onConflict();
          }
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onClose={() => !replaceProof.isPending && onClose()}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 600 }}>
        {t("payments.replace.title")}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {t("payments.replace.body")}
        </DialogContentText>
        <ProofFileInput
          value={proof}
          onChange={setProof}
          disabled={replaceProof.isPending}
        />
        {errorKey && (
          <Typography variant="body2" sx={{ mt: 1, color: colorTextError }}>
            {t(errorKey, { size: MAX_PROOF_SIZE_MB })}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <CustomButton
          color="inverted"
          onClick={onClose}
          disabled={replaceProof.isPending}
        >
          {t("payments.replace.cancel")}
        </CustomButton>
        <CustomLoadingButton
          loading={replaceProof.isPending}
          disabled={!proof || replaceProof.isPending}
          onSubmit={handleSubmit}
          label={t("payments.replace.submit")}
          fullwidth={false}
        />
      </DialogActions>
    </Dialog>
  );
};
